import { useCallback, useState } from 'react';
import { IGif } from '@giphy/js-types';
import { Compress } from '@frontend/compress';
import { StreamGiphyType } from '@frontend/gif-picker';
import { useTranslation } from '@frontend/i18n';
import { ThreadSendingMediaItem } from '@frontend/integrated-messaging';
import { genUUIDV4 } from '@frontend/string';
import { useAlert } from '@frontend/design-system';
import { MAX_FILE_SIZE } from '../constants';
import { parseGiphyToStreamGifAttachment } from '../utils';

// Create compress instance with same config as Avatar component
const compress = new Compress({
  targetSize: 0.8,
  quality: 0.9,
  maxWidth: 800,
  maxHeight: 600,
});

export type UpdatedThreadSendingMediaItem = ThreadSendingMediaItem & {
  uploadable: boolean;
  type?: string;
  meta?: StreamGiphyType;
};

interface UseFilesUpload {
  hideUploadingFilesEffect: () => void;
  files: UpdatedThreadSendingMediaItem[];
  removeFile: (id: string) => void;
  setFiles: (files?: File[]) => Promise<void>;
  showUploadingFilesEffect: () => void;
  addGif: (gif: IGif) => void;
}

type MediaParams = { file: File } | { url: string; meta: StreamGiphyType };

const convertDataToMedia = (input: MediaParams): UpdatedThreadSendingMediaItem => {
  const isInputAFile = 'file' in input;

  return {
    file: isInputAFile ? input.file : new File([], ''),
    hasError: false,
    id: isInputAFile ? genUUIDV4() : input.url,
    mediaObj: {
      filename: isInputAFile ? input.file?.name : '',
      mediaId: isInputAFile ? genUUIDV4() : input.url,
      url: isInputAFile ? URL.createObjectURL(input.file) : input.url,
    },
    previewSrc: isInputAFile ? URL.createObjectURL(input.file) : input.url,
    uploading: false,
    uploadable: isInputAFile,
    meta: !isInputAFile ? input.meta : undefined,
    type: isInputAFile ? input.file?.type : undefined,
  };
};

export const useFilesUpload = (isMultipleExtensionAllowed = false): UseFilesUpload => {
  const alert = useAlert();
  const { t } = useTranslation('team-chat');

  const [selectedFiles, setSelectedFiles] = useState<UpdatedThreadSendingMediaItem[]>([]);

  const setFiles = useCallback(
    async (files?: File[]) => {
      if (!files?.length) {
        setSelectedFiles([]);
        return;
      }

      if (files.length > 10) {
        alert.error(
          t('You can only attach up to 10 files at a time. {{count}} files were ignored.', {
            count: files.length - 10,
          })
        );
      }

      // First filter out files that are too large (before compression)
      const filesToProcess = files.slice(0, 10);
      const validFiles = filesToProcess.filter((file) => file.size <= MAX_FILE_SIZE);
      const imageFiles = validFiles.filter((file) => file.type.startsWith('image/'));
      const nonImageFiles = validFiles.filter((file) => !file.type.startsWith('image/'));
      const removedFiles = filesToProcess.filter((file) => file.size > MAX_FILE_SIZE);

      if (removedFiles.length > 0) {
        const errorMessage = isMultipleExtensionAllowed
          ? t('You can only upload files that are less than 10 MB in size. {{count}} files were not uploaded.', {
              count: removedFiles.length,
            })
          : t('You can only upload images that are less than 10 MB in size. {{count}} images were not uploaded.', {
              count: removedFiles.length,
            });
        alert.error(errorMessage);
      }

      try {
        // Compress only the valid files
        const compressedResults = await compress.compress(imageFiles);

        // Convert compressed files to uploadable media items
        const uploadableFiles = [
          ...compressedResults.map((result) =>
            convertDataToMedia({ file: new File([result.photo.data], result.photo.name, { type: result.photo.type }) })
          ),
          ...(isMultipleExtensionAllowed ? nonImageFiles.map((file) => convertDataToMedia({ file })) : []),
        ];

        setSelectedFiles([...selectedFiles, ...uploadableFiles]);
      } catch (compressionError) {
        console.error('Image compression failed:', compressionError);
        // Fallback to original logic if compression fails
        const uploadableFiles = validFiles.map((file) => convertDataToMedia({ file }));
        setSelectedFiles([...selectedFiles, ...uploadableFiles]);
      }
    },
    [selectedFiles, isMultipleExtensionAllowed, alert, t]
  );

  const removeFile = useCallback(
    (id: string) => {
      setSelectedFiles(selectedFiles.filter((file) => file.id !== id));
    },
    [selectedFiles]
  );

  const toggleUploadingEffect = useCallback((uploading: boolean) => {
    setSelectedFiles((files = []) => files.map((file) => ({ ...file, uploading })));
  }, []);

  return {
    hideUploadingFilesEffect: () => toggleUploadingEffect(false),
    files: selectedFiles,
    removeFile,
    setFiles,
    showUploadingFilesEffect: () => toggleUploadingEffect(true),
    addGif: (gif: IGif) => {
      const meta = parseGiphyToStreamGifAttachment(gif);
      setSelectedFiles([...selectedFiles, convertDataToMedia({ url: meta.thumb_url, meta })]);
    },
  };
};
